import { type VirtualBackgroundsProps as VirtualBackgroundStatelessProps } from '../../call/virtualBackgrounds';
import {
  useLocalMedia,
  useLocalMediaActions,
  useLocalMediaVideoElementCopy,
} from '../../../hooks';
import { track } from '../../../features/tracking';
import { TrackedEvents } from '../../../features/tracking/track';
import { useState } from 'react';
import { getLogger } from '../../../libs/logger';
import { useAppDispatch } from '../../../hooks/useReduxToolkit';
import { closeModal } from '../../../features/modals/modals.slice';
import { ModalId } from '../../../features/modals/modals.type';
import {
  getVirtualBackgroundFromLocalStorage,
  setVirtualBackgroundToLocalStorage,
} from './virtualBackgroundStorageHelper';
import { VirtualBackground, backgrounds } from './constants';
import { useGetUserId } from '../../../hooks/useCredentials';
import { toast } from '@doxyme/ui/components/foundations';
import { useIntl } from '@doxyme/locale';

const logger = getLogger('useVirtualBackgroundState');

export const useVirtualBackgroundState =
  (): VirtualBackgroundStatelessProps => {
    const { videoElement } = useLocalMediaVideoElementCopy();
    const { toggleVideo, applyVirtualBackground } = useLocalMediaActions();
    const { hasVideo, hasVideoAccess } = useLocalMedia();
    const [isApplyingVirtualBackground, setIsApplyingVirtualBackground] =
      useState(false);
    const userId = useGetUserId();
    const dispatch = useAppDispatch();
    const [currentBackground, setCurrentBackground] = useState(
      getVirtualBackgroundFromLocalStorage(userId),
    );
    const { formatMessage } = useIntl();

    const onSelectVirtualBackground = async (
      background: VirtualBackground,
    ): Promise<void> => {
      if (hasVideoAccess) {
        setIsApplyingVirtualBackground(true);

        try {
          await applyVirtualBackground(background.filter);

          setVirtualBackgroundToLocalStorage(background, userId);

          setCurrentBackground(background);
        } catch (error) {
          toast.error({
            title: formatMessage({ id: 'virtualBackground.error' }),
          });

          logger.error('Error applying virtual background', {
            backgroundIndex: background.index,
            backgroundName: background.name,
            error,
          });
        } finally {
          setIsApplyingVirtualBackground(false);
        }
      }

      if (!hasVideo) {
        await toggleVideo();
      }

      void track(TrackedEvents.BackgroundEffectApplied, {
        backgroundEffect: background.name === 'None' ? null : background.name,
      });

      // Added eslint-disable lines due to saving compatibility with OG
      void track(TrackedEvents.ButtonClicked, {
        button_text: 'Apply background effect',

        button_action: background?.name,
      });
    };

    const onClose = () => dispatch(closeModal(ModalId.VirtualBackground));

    return {
      hasVideo,
      hasVideoAccess,
      backgrounds,
      videoElement,
      storedBackground: currentBackground as VirtualBackground,
      onSelectVirtualBackground,
      onClose,
      isApplyingVirtualBackground,
    };
  };
